import './polyfills' // Must be first
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from '@components/App'
import { devLog } from '@methods/devLog'
import { configureAwsRum } from '@methods/awsRum'
import { initXS2A } from '@methods/loadXS2A'
import { setupAxiosInterceptors } from '@methods/setupAxiosInterceptors'
import { setupFlagEmojiPolyfill } from '@methods/flagEmoji'
import { NODE_ENV, isLocalDev } from '@constants/envs'
import { getUrlParam } from '@methods/urlParams'
import { rootStore } from '@store/Root.store'
import { ThemeScheme } from '@/themes'
import { applyTheme } from '@/themes/apply-theme'
import { captureGocardlessSessionToken } from '@methods/gocardlessSessionToken'

async function enableMocking() {
  if (!isLocalDev) {
    return
  }

  const { worker } = await import('./mocks/browser')
  return worker.start({
    onUnhandledRequest: 'bypass',
  })
}

// Must run before anything else reads or reports the URL: it moves the
// GoCardless session token out of the visible URL into session storage.
captureGocardlessSessionToken()

configureAwsRum()
initXS2A()
setupAxiosInterceptors()
// Windows has no colour flag emoji: inject the Twemoji flag font before React mounts.
setupFlagEmojiPolyfill()

if (NODE_ENV === 'production') {
  devLog('*****PRODUCTION BUILD*****')
} else {
  devLog('NODE_ENV:', NODE_ENV)
}

// Theme initialization must happen before React mounts to avoid a flash of the
// default theme. Both the MobX store and the DOM `data-theme` attribute are set
// synchronously here so the correct CSS custom properties are active on first paint.
const themeId = getUrlParam('themeId')
if (themeId?.length) {
  devLog('Set themeId based on param: ', themeId)
  const scheme = (
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  ) as ThemeScheme
  rootStore.InterfaceState.setTheme(scheme, themeId)
}
applyTheme(rootStore.InterfaceState.themeId)

enableMocking().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  )
})
